/* HTTP client for the ASA Gateway JSON API.
 *
 * The API is REST/JSON over HTTPS and reports errors as RFC 9457 problem
 * documents (AD-22). The `type` suffix is a CONTRACT the frontend maps to i18n
 * keys — renaming one on the server is a breaking change, not a refactor.
 *
 * Sessions are server-side: an opaque token in an httpOnly cookie, so a
 * Super-admin deactivating an account takes effect immediately (AD-21). There
 * is no token in JS to attach; we send credentials and nothing else.
 */

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api'

/** An RFC 9457 problem document. */
export interface Problem {
  type: string
  title: string
  status: number
  detail?: string
  instance?: string
  /** Field-level validation failures, keyed by field path. */
  errors?: Record<string, string[]>
}

export class ApiError extends Error {
  readonly status: number
  readonly problem: Problem

  constructor(problem: Problem) {
    super(problem.detail ?? problem.title)
    this.name = 'ApiError'
    this.status = problem.status
    this.problem = problem
  }

  /** The trailing segment of the problem type, which is the i18n key. */
  get code(): string {
    const segments = this.problem.type.split('/')
    return segments[segments.length - 1] ?? 'unknown'
  }

  get isUnauthenticated(): boolean {
    return this.status === 401
  }

  /** 403 — authenticated, but the role or scope forbids it. */
  get isForbidden(): boolean {
    return this.status === 403
  }

  /** 404 is returned for both missing and not-owned, so existence is never
   *  leaked. Do not tell the user the record "exists but is yours to request". */
  get isNotFound(): boolean {
    return this.status === 404
  }
}

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown
  /** Appended as a query string; null and undefined values are dropped. */
  params?: Record<string, string | number | boolean | null | undefined>
}

function buildUrl(path: string, params?: RequestOptions['params']): string {
  const url = `${BASE_URL}${path.startsWith('/') ? path : `/${path}`}`
  if (!params) return url

  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value !== null && value !== undefined && value !== '') {
      search.append(key, String(value))
    }
  }
  const qs = search.toString()
  return qs ? `${url}?${qs}` : url
}

async function toProblem(response: Response): Promise<Problem> {
  // A gateway, proxy or panic can produce a non-JSON error body. Synthesise a
  // problem rather than throwing a parse error over the real failure.
  try {
    const data = (await response.json()) as Partial<Problem>
    if (data && typeof data.type === 'string') {
      return { status: response.status, title: response.statusText, ...data } as Problem
    }
  } catch {
    /* fall through to the synthetic problem below */
  }
  return {
    type: 'about:blank',
    title: response.statusText || 'Request failed',
    status: response.status,
  }
}

export async function request<T>(
  path: string,
  { body, params, headers, ...init }: RequestOptions = {},
): Promise<T> {
  const response = await fetch(buildUrl(path, params), {
    ...init,
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...headers,
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  })

  if (!response.ok) {
    throw new ApiError(await toProblem(response))
  }

  if (response.status === 204) {
    return undefined as T
  }

  return (await response.json()) as T
}

export const api = {
  get: <T>(path: string, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'POST', body }),
  patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'PUT', body }),
  delete: <T>(path: string, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'DELETE' }),
}

/** List responses are always `{"items": [...]}`, allocated so empty is `[]`. */
export interface ListResponse<T> {
  items: T[]
  total?: number
}
